// Country list + SVG flag set, sourced from the already-installed
// `react-phone-number-input` package. Maps a tenant's ISO code (or
// its billing-currency proxy for legacy rows) onto a flag + English
// name. Shared by the Companies, AI Usage, and Storage platform-admin
// tables so all three render the Country column identically.
import countryLabels from 'react-phone-number-input/locale/en.json'
import Flags from 'react-phone-number-input/flags'

/** Currency → country metadata table for the LEGACY fallback path.
 *  Used only when the real `country` field is null (rows from before
 *  the country-detection wiring shipped). New tenants always get a
 *  real country at signup or first payment, so this map will only
 *  ever be hit for old data. */
const COUNTRY_META_BY_CURRENCY = {
  INR: { code: 'IN', label: 'India' },
  AED: { code: 'AE', label: 'UAE' },
  USD: { code: 'GLOBAL', label: 'Global' },
}

export function CountryCell({ country, billingCurrency }) {
  // Tier 1: real country code on the tenant.
  if (country && typeof country === 'string') {
    const iso = country.toUpperCase()
    const label = countryLabels[iso] ?? iso
    const FlagSvg = Flags[iso]
    return (
      <span
        className="inline-flex items-center gap-2 text-sm text-[color:var(--color-ink)] whitespace-nowrap"
        title={label}
      >
        {FlagSvg ? (
          <FlagSvg
            title={label}
            style={{ width: 18, height: 12, flexShrink: 0 }}
          />
        ) : (
          // Fall back to a small globe glyph for any ISO code the
          // phone-number-input flag set happens not to ship.
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center w-[18px] h-[12px] text-[10px]"
          >
            🌐
          </span>
        )}
        <span>{label}</span>
      </span>
    )
  }
  // Tier 2 (no real country on the tenant): render UNSET — never the
  // billing-currency proxy as if it were a selected country. The old
  // proxy rendering was pixel-identical to a real value, so the table
  // showed "🇮🇳 India" while the Edit dialog for the same tenant said
  // "Not set" — the cell asserted data nobody ever selected
  // (QA-reported). The proxy survives only as a tooltip hint so an
  // operator can still see where a legacy tenant likely is, and fix
  // the real value via Edit Company. The country FILTER still matches
  // legacy rows through the same proxy server-side, so they remain
  // findable even while displaying the dash.
  const meta = COUNTRY_META_BY_CURRENCY[billingCurrency]
  return (
    <span
      className="text-[color:var(--color-ink-muted)]"
      title={
        meta
          ? `No country set — billing currency suggests ${meta.label}. Set it via Edit Company.`
          : 'No country set.'
      }
    >
      —
    </span>
  )
}
